import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import SectionCard from '../../../components/SectionCard';
import StatCard from '../../../components/StatCard';
import StatusChip from '../../../components/StatusChip';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { getPreAuthRequests, updatePreAuth } from '../../../api/pharmacy';
import { formatCurrency } from '../../../utils';
import { mapList, mapPreAuth } from '../../../utils/pharmacyMappers';
import usePharmacyLoad from '../../../hooks/usePharmacyLoad';
import { IPD_COLORS } from '../../../theme';
import { fadeInSx } from '../pharmacyStyles';
import PharmacyTableState from '../PharmacyTableState';

export default function PreAuthTab() {
  const [dialog, setDialog] = useState(null);
  const [actioning, setActioning] = useState(false);

  const { data, loading, error, reload } = usePharmacyLoad(
    () => getPreAuthRequests(),
    [],
  );

  const items = useMemo(() => mapList(data, mapPreAuth), [data]);
  const pending = items.filter((i) => i.status === 'PENDING_INSURANCE').length;

  async function handleDecision(approved) {
    if (!dialog) return;
    setActioning(true);
    try {
      await updatePreAuth(dialog.id, {
        status: approved ? 'APPROVED' : 'REJECTED',
        action_by: sessionStorage.getItem('USER_ACCESS_KEY') || undefined,
      });
      toast.success(approved ? 'Insurance pre-authorization approved' : 'Pre-authorization rejected');
      setDialog(null);
      reload();
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    } finally {
      setActioning(false);
    }
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard label="Pending Insurance" value={pending} icon={VerifiedUserIcon} tone="amber" loading={loading} />
        <StatCard label="Approved" value={items.filter((i) => i.status === 'APPROVED').length} icon={VerifiedUserIcon} tone="mint" loading={loading} />
        <StatCard label="Total Requests" value={items.length} icon={VerifiedUserIcon} tone="teal" loading={loading} />
      </Box>

      <SectionCard title="Insurance Pre-Authorization" subtitle="Dispensed transactions awaiting insurer approval">
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={!loading && !error && items.length === 0}
          emptyMessage="No pre-authorization requests found."
          onRetry={reload}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  {['Request ID', 'Dispense', 'Patient', 'Insurer', 'Amount', 'Submitted', 'Status', 'Actions'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                    <td style={{ padding: '12px', fontWeight: 600, color: IPD_COLORS.primary }}>{row.id}</td>
                    <td style={{ padding: '12px' }}>{row.dispense_id}</td>
                    <td style={{ padding: '12px' }}>{row.patient_name}</td>
                    <td style={{ padding: '12px' }}>{row.insurer}</td>
                    <td style={{ padding: '12px', fontWeight: 600 }}>{formatCurrency(row.amount)}</td>
                    <td style={{ padding: '12px', fontSize: 13 }}>{row.submitted_at}</td>
                    <td style={{ padding: '12px' }}><StatusChip status={row.status} /></td>
                    <td style={{ padding: '12px' }}>
                      {row.status === 'PENDING_INSURANCE' ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Approve">
                            <IconButton size="small" sx={{ color: IPD_COLORS.success.text }}
                              onClick={() => setDialog({ ...row, action: 'approve' })}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" sx={{ color: IPD_COLORS.error.text }}
                              onClick={() => setDialog({ ...row, action: 'reject' })}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </PharmacyTableState>
      </SectionCard>

      <ConfirmDialog
        open={Boolean(dialog)}
        title={dialog?.action === 'approve' ? 'Approve Pre-Authorization' : 'Reject Pre-Authorization'}
        description={dialog ? `${dialog.action === 'approve' ? 'Approve' : 'Reject'} insurance pre-auth for ${dialog.patient_name} (${formatCurrency(dialog.amount)})?` : ''}
        destructive={dialog?.action === 'reject'}
        loading={actioning}
        onConfirm={() => handleDecision(dialog?.action === 'approve')}
        onCancel={() => setDialog(null)}
      />
    </Box>
  );
}
